/**
 * Student year-based feature eligibility (client mirror).
 *
 * Every UI decision about Placement Hub and Projects visibility goes through
 * this module. Nothing else in the frontend derives a program year, and nothing
 * else parses a hall ticket.
 *
 * The backend is authoritative. `buildUserResponse` resolves eligibility
 * server-side and ships it as `featureEligibility` on the login / session
 * payload, so the normal path is a straight read of a precomputed answer. The
 * local derivation below exists for sessions established before this field
 * shipped, where `academicProfile` is present but `featureEligibility` is not.
 *
 * Hiding UI is a usability decision, never a security boundary — the same rules
 * are enforced on the API by `middleware/studentEligibilityMiddleware`.
 *
 * Note the file extension: this project is JavaScript with JSDoc types and has
 * no TypeScript toolchain, so this is `.js` rather than the `.ts` named in the
 * original brief.
 */

/** Program year at which placement-oriented features unlock. */
export const PLACEMENT_ELIGIBLE_FROM_YEAR = 3;

/** Program year at which project features unlock. */
export const PROJECTS_ELIGIBLE_FROM_YEAR = 3;

const MAX_PROGRAM_YEAR = 4;

/** Regular (1A) starts in year 1; Lateral (5A) enters directly into year 2. */
const ENTRY_OFFSET = Object.freeze({ '1a': 1, '5a': 3 });

/** `25wh1a0474` / `26wh5a0405`, and the legacy compact `241a05001`. */
const HALL_TICKET_WH = /^(\d{2})wh(1a|5a)(\d{2})([0-9a-f]+)$/i;
const HALL_TICKET_LEGACY = /^(\d{2})(1a|5a)(\d{2})(\d+)$/i;

/**
 * Mirrors backend `parseStudentEmail.computeSemesterAndYear`. The June rollover
 * is what makes "admission year + 1" wrong for half the calendar year: a 2025
 * entrant is in year 1 until June 2026 and year 2 from June 2026.
 *
 * @param {number} joinYear
 * @param {'1a'|'5a'} entryToken
 * @param {Date} refDate
 * @returns {number}
 */
function programYearFrom(joinYear, entryToken, refDate) {
  const entryOffset = ENTRY_OFFSET[entryToken];
  if (entryOffset == null) return 1;

  const semesterAdjustment = refDate.getMonth() >= 5 ? 1 : 0;
  let semester =
    (refDate.getFullYear() - joinYear) * 2 + entryOffset - 1 + semesterAdjustment;

  if (semester < 1) semester = 1;
  if (semester > 8) semester = 8;

  return Math.ceil(semester / 2);
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function toProgramYear(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.trunc(n);
  if (rounded < 1 || rounded > MAX_PROGRAM_YEAR) return null;
  return rounded;
}

/**
 * @param {unknown} entryType
 * @returns {'1a' | '5a' | null}
 */
function entryToken(entryType) {
  if (entryType === 'Lateral' || entryType === '5A') return '5a';
  if (entryType === 'Regular' || entryType === '1A') return '1a';
  return null;
}

/**
 * @param {unknown} rollNumber
 * @param {Date} refDate
 * @returns {number | null}
 */
function yearFromRollNumber(rollNumber, refDate) {
  if (typeof rollNumber !== 'string') return null;

  const trimmed = rollNumber.trim().toLowerCase();
  if (!trimmed) return null;

  const at = trimmed.indexOf('@');
  const local = at > 0 ? trimmed.slice(0, at) : trimmed;

  const match = local.match(HALL_TICKET_WH) || local.match(HALL_TICKET_LEGACY);
  if (!match) return null;

  const yy = Number.parseInt(match[1], 10);
  if (!Number.isFinite(yy) || yy < 0 || yy > 99) return null;

  const token = String(match[2]).toLowerCase();
  if (token !== '1a' && token !== '5a') return null;

  return toProgramYear(programYearFrom(2000 + yy, token, refDate));
}

/**
 * Flatten the shapes the client actually holds — the auth user, its nested
 * `academicProfile`, and (when present) a student profile — into one subject.
 *
 * @param {Record<string, any> | null | undefined} user
 * @returns {Record<string, any>}
 */
function toSubject(user) {
  if (!user || typeof user !== 'object') return {};
  const academic = user.academicProfile || {};
  const profile = user.studentProfile || user.codingAccess?.profile || {};

  return {
    role: user.role ?? null,
    currentYear: academic.currentYear ?? user.currentYear ?? null,
    year: profile.year ?? null,
    joinYear: academic.joinYear ?? user.joinYear ?? null,
    entryType: academic.entryType ?? user.entryType ?? null,
    rollNumber: profile.rollNumber ?? academic.rollNumber ?? user.rollNumber ?? null,
    email: user.email ?? null,
    placementHubEnabled: profile.placementHubEnabled ?? null,
    projectsEnabled: profile.projectsEnabled ?? null,
    featureEligibility: user.featureEligibility ?? null,
  };
}

/**
 * Resolve a student's current program year.
 *
 * Priority: explicit stored year → admission cohort → hall ticket.
 *
 * @param {Record<string, any> | null | undefined} user
 * @param {{ refDate?: Date }} [options]
 * @returns {number | null} 1–4, or null when no signal resolves
 */
export function getCurrentYear(user, options = {}) {
  const subject = toSubject(user);
  const refDate = options.refDate instanceof Date ? options.refDate : new Date();

  const explicit = toProgramYear(subject.currentYear) ?? toProgramYear(subject.year);
  if (explicit != null) return explicit;

  const token = entryToken(subject.entryType);
  const joinYear = Number(subject.joinYear);
  if (token != null && Number.isFinite(joinYear) && joinYear >= 1900) {
    return toProgramYear(programYearFrom(joinYear, token, refDate));
  }

  return yearFromRollNumber(subject.rollNumber ?? subject.email, refDate);
}

/**
 * @param {unknown} value
 * @returns {boolean | null}
 */
function readOverride(value) {
  if (value === true) return true;
  if (value === false) return false;
  return null;
}

/**
 * @param {Record<string, any> | null | undefined} user
 * @param {'placementHub' | 'projects'} feature
 * @param {{ refDate?: Date }} [options]
 * @returns {boolean}
 */
function canAccess(user, feature, options = {}) {
  const subject = toSubject(user);

  // Year gating applies to students only. A missing role is treated as a student
  // so an unresolved session cannot flash placement UI before it settles.
  if (subject.role && subject.role !== 'student') return true;

  // Server-resolved answer wins when the session carries one.
  const server = subject.featureEligibility;
  if (server && typeof server === 'object' && typeof server[feature] === 'boolean') {
    return server[feature];
  }

  const override = readOverride(
    feature === 'placementHub' ? subject.placementHubEnabled : subject.projectsEnabled,
  );
  if (override !== null) return override;

  const minimumYear =
    feature === 'placementHub' ? PLACEMENT_ELIGIBLE_FROM_YEAR : PROJECTS_ELIGIBLE_FROM_YEAR;

  const year = getCurrentYear(user, options);
  if (year == null) return false;

  return year >= minimumYear;
}

/**
 * @param {Record<string, any> | null | undefined} user
 * @param {{ refDate?: Date }} [options]
 * @returns {boolean}
 */
export function canAccessPlacementHub(user, options = {}) {
  return canAccess(user, 'placementHub', options);
}

/**
 * @param {Record<string, any> | null | undefined} user
 * @param {{ refDate?: Date }} [options]
 * @returns {boolean}
 */
export function canAccessProjects(user, options = {}) {
  return canAccess(user, 'projects', options);
}

/**
 * Both decisions plus the resolved year, for components that need all of it.
 *
 * @param {Record<string, any> | null | undefined} user
 * @param {{ refDate?: Date }} [options]
 * @returns {{ currentYear: number|null, placementHub: boolean, projects: boolean }}
 */
export function describeEligibility(user, options = {}) {
  return {
    currentYear: getCurrentYear(user, options),
    placementHub: canAccessPlacementHub(user, options),
    projects: canAccessProjects(user, options),
  };
}

export default {
  PLACEMENT_ELIGIBLE_FROM_YEAR,
  PROJECTS_ELIGIBLE_FROM_YEAR,
  getCurrentYear,
  canAccessPlacementHub,
  canAccessProjects,
  describeEligibility,
};
